import React, { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

/**
 * <DatePickerInput> — campo data ibrido:
 *   • Input testuale nativo (formato yyyy-mm-dd, stesso comportamento di prima)
 *   • Bottone calendario che apre un popover con selezione visuale
 *
 * L'onChange restituisce sempre una stringa yyyy-mm-dd (o "" se svuotato) per
 * mantenere piena compatibilità con i form esistenti che usavano
 * `<Input type="date">`.
 *
 * Props aggiuntive rispetto al vecchio input:
 *   - min / max: stringhe yyyy-mm-dd → limiti sia sull'input che sul calendario
 *   - fromYear / toYear: opzionali (per selettori anni futuri/passati)
 */
export default function DatePickerInput({
  value = "",
  onChange,
  min,
  max,
  disabled = false,
  className = "",
  testid,
  placeholder = "gg/mm/aaaa",
  fromYear,
  toYear,
}) {
  const [open, setOpen] = useState(false);

  const asDate = (iso) => {
    if (!iso) return undefined;
    const s = String(iso).slice(0, 10);
    // Costruiamo la data a mezzogiorno UTC per evitare shift di fuso locale
    const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return undefined;
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  };
  const toIso = (dt) => {
    if (!dt) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const emit = (iso) => { if (onChange) onChange({ target: { value: iso } }); };

  return (
    <div className="flex gap-1 min-w-0">
      <Input
        data-testid={testid}
        type="date"
        value={value || ""}
        min={min || undefined}
        max={max || undefined}
        disabled={disabled}
        onChange={(e) => emit(e.target.value)}
        placeholder={placeholder}
        className={`min-w-0 ${className || "bg-zinc-950 border-zinc-800"}`}
      />
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            data-testid={testid ? `${testid}-calendar` : undefined}
            className="border-zinc-800 shrink-0"
            title="Apri calendario"
          >
            <CalendarIcon className="h-4 w-4 text-zinc-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[color:var(--bg-surface,#141416)] border-zinc-800" align="end">
          <Calendar
            mode="single"
            selected={asDate(value)}
            onSelect={(d) => { emit(toIso(d)); setOpen(false); }}
            fromDate={asDate(min)}
            toDate={asDate(max)}
            fromYear={fromYear}
            toYear={toYear}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
